/**
 * Procedural sound effects — no asset files, all synthesised with Web Audio.
 * Stand-ins for the 2013 `sonDestruction.wav` / `SonAttrapeBonus.mp3` / `SonBoost.mp3`.
 *
 * The AudioContext is created lazily and resumed on the first user gesture
 * (browser autoplay policy).
 */

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = false;

  /** Call from a user-gesture handler to unlock audio. */
  unlock(): void {
    this.ensure();
    void this.ctx?.resume();
  }

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.35;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  private env(node: AudioNode, start: number, attack: number, release: number, peak = 1): GainNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(peak, start + attack);
    g.gain.exponentialRampToValueAtTime(0.001, start + attack + release);
    node.connect(g);
    g.connect(this.master!);
    return g;
  }

  private tone(type: OscillatorType, from: number, to: number, dur: number, peak = 1): void {
    const ctx = this.ensure();
    if (!ctx || this.muted) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t + dur);
    this.env(osc, t, 0.005, dur, peak);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private noise(dur: number, peak = 1): void {
    const ctx = this.ensure();
    if (!ctx || this.muted) return;
    const t = ctx.currentTime;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(2400, t);
    filter.frequency.exponentialRampToValueAtTime(200, t + dur);
    src.connect(filter);
    this.env(filter, t, 0.005, dur, peak);
    src.start(t);
    src.stop(t + dur + 0.05);
  }

  crash(): void {
    this.noise(0.4, 1);
    this.tone("sine", 140, 40, 0.35, 0.8);
  }

  pickup(): void {
    this.tone("square", 520, 880, 0.14, 0.5);
  }

  boost(): void {
    this.tone("sawtooth", 180, 900, 0.35, 0.5);
  }

  beep(): void {
    this.tone("square", 440, 440, 0.09, 0.4);
  }

  go(): void {
    this.tone("square", 660, 880, 0.22, 0.5);
  }

  win(): void {
    this.tone("triangle", 523, 523, 0.12, 0.5);
    setTimeout(() => this.tone("triangle", 784, 784, 0.24, 0.5), 110);
  }
}
